import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useLatestRequest } from '@/composables/latestRequest'
import { usePermission } from '@/composables/permission'
import { useApiStore } from '@/stores/api'
import { useFlagStore } from '@/stores/flag'
import { useSettingsStore } from '@/stores/settings'

import { hotReloadStore } from '@/utils/build'

import type { PermissionFallback, PermissionName } from '@/composables/permission'
import type { GetCapabilitiesRequest } from '@policyfly/protobuf/patrick'
import type { Capability, Condition, Status } from '@policyfly/protobuf/pfcapability'
import type { ComputedRef, Ref } from 'vue'

export interface CapabilityStore {
  /**
   * The capabilities available for the most recently loaded request.
   */
  capabilities: Ref<Capability[]>
  /**
   * The conditions affecting the object of the most recently loaded request.
   */
  conditions: Ref<Condition[]>
  /**
   * Whether pfcapability support is enabled, which requires a prestige program and the `pfcapability-enabled` flag.
   * Whilst disabled, every check returns its fallback instead.
   */
  enabled: ComputedRef<boolean>
  /**
   * Checks if a capability is available for the most recently loaded request.
   *
   * @param capability The capability to check if the user has.
   * @param fallback The permission (and item, if applicable) to check instead whilst pfcapability support is disabled.
   * Omit for capabilities with no equivalent permission, which are then denied whilst pfcapability support is disabled.
   */
  checkCapability: <T extends PermissionName>(capability: Capability, fallback?: PermissionFallback<T>) => boolean
  /**
   * Returns the first condition matching the provided status that affects the object of the most recently loaded request,
   * or `null` if there is no match.
   *
   * @param status The status to check for.
   * @param fallback The condition (or `null`) to return instead whilst pfcapability support is disabled.
   */
  checkCondition: (status: Status, fallback: Condition | null) => Condition | null
  /**
   * Loads the capabilities for a request, replacing any previously loaded capabilities.
   * Any load still in flight is aborted, so only the latest request is applied.
   * Legacy programs have no capabilities, so no request is made and they are cleared instead.
   *
   * @param request The object to load capabilities for, or omit to only load global capabilities.
   */
  load: (request?: GetCapabilitiesRequest) => Promise<void>
  /**
   * Reloads the capabilities for the most recently loaded request.
   */
  reload: () => Promise<void>
}

/**
 * Holds the capabilities available to the user on the current page.
 */
export const useCapabilityStore = defineStore('capability', (): CapabilityStore => {
  const capabilities: CapabilityStore['capabilities'] = ref([])
  const conditions: CapabilityStore['conditions'] = ref([])

  const flagStore = useFlagStore()
  const settingsStore = useSettingsStore()
  const enabled: CapabilityStore['enabled'] = computed(() => {
    if (!settingsStore.protobuf) return false
    return flagStore.getFlag('pfcapability-enabled').enabled
  })

  const { checkPermission } = usePermission()
  const checkCapability: CapabilityStore['checkCapability'] = (capability, fallback) => {
    if (enabled.value) {
      return capabilities.value.includes(capability)
    }
    if (!fallback) return false
    const [permission, item] = fallback
    return checkPermission(permission, item)
  }

  const checkCondition: CapabilityStore['checkCondition'] = (status, fallback) => {
    if (!enabled.value) return fallback
    return conditions.value.find((condition) => condition.status === status) ?? null
  }

  let lastRequest: GetCapabilitiesRequest | undefined
  const apiStore = useApiStore()
  const { run: loadCapabilities, abort: abortCapabilities } = useLatestRequest({
    request: (controller, request?: GetCapabilitiesRequest) => apiStore.capability.list(request, controller),
    onSuccess: (response) => {
      capabilities.value = response.capabilities
      conditions.value = response.conditions
    },
    onError: (err) => {
      console.error(err)
      capabilities.value = []
      conditions.value = []
    },
  })
  const load: CapabilityStore['load'] = async (request) => {
    lastRequest = request
    // legacy programs have no capabilities upstream, so the request would always error
    if (!settingsStore.protobuf) {
      abortCapabilities()
      capabilities.value = []
      conditions.value = []
      return
    }
    await loadCapabilities(request)
  }

  const reload: CapabilityStore['reload'] = async () => {
    await load(lastRequest)
  }

  return {
    capabilities,
    conditions,
    enabled,
    checkCapability,
    checkCondition,
    load,
    reload,
  }
})

hotReloadStore(useCapabilityStore)
